import React, { FC } from 'react';
import { Box } from '@mui/material';
import { Image, Text, View } from '@react-pdf/renderer';
import { SQUAD_COLORS } from './templateConstants';
import { SQUAD_TYPOGRAPHY, SQUAD_TYPOGRAPHY_PDF } from './pageHeaderTokens';
import previewStyles from './PdfPreview/sharedStyles';
import {
  buildAdvisorBlocks,
  SquadAdvisorBlock,
  SquadAdvisorRecord,
  SQUAD_ADVISOR_PHOTO,
} from './squadAdvisors';
import { RichHtmlPdf, richHtmlForPreview } from './squadRichText';

const renderCompanyMark = (
  block: Extract<SquadAdvisorBlock, { type: 'featured' }>,
  primary: string,
  secondary: string,
  pdf = false,
  styles?: any,
) => {
  if (block.logoUrl) {
    return pdf ? (
      <Image src={block.logoUrl} style={{ width: 88, height: 36, objectFit: 'contain' }} />
    ) : (
      <Box component="img" src={block.logoUrl} alt={block.company || 'Company logo'} sx={{ maxWidth: '100%', maxHeight: 28, objectFit: 'contain' }} />
    );
  }

  if (block.companyLine1 || block.companyLine2) {
    return pdf ? (
      <View style={{ alignItems: 'flex-start' }}>
        {block.companyLine1 ? (
          <Text style={styles?.advisorFeaturedLogoLine1 || { fontFamily: 'SegoeUI', fontSize: SQUAD_TYPOGRAPHY_PDF.advisorLogoLine1, color: primary, textAlign: 'left' }}>
            {block.companyLine1}
          </Text>
        ) : null}
        {block.companyLine2 ? (
          <Text style={styles?.advisorFeaturedLogoLine2 || { fontFamily: 'SegoeUI', fontSize: SQUAD_TYPOGRAPHY_PDF.advisorLogoLine2, color: secondary, textAlign: 'left' }}>
            {block.companyLine2}
          </Text>
        ) : null}
      </View>
    ) : (
      <Box sx={{ textAlign: 'left', minWidth: 0 }}>
        {block.companyLine1 ? (
          <Box sx={{ ...previewStyles.advisorFeaturedLogoLine1, color: primary }}>{block.companyLine1}</Box>
        ) : null}
        {block.companyLine2 ? (
          <Box sx={{ ...previewStyles.advisorFeaturedLogoLine2, color: secondary }}>{block.companyLine2}</Box>
        ) : null}
      </Box>
    );
  }

  if (block.company) {
    return pdf ? (
      <Text style={{ fontSize: SQUAD_TYPOGRAPHY_PDF.advisorCompany, color: secondary }}>{block.company}</Text>
    ) : (
      <Box sx={{ fontSize: `${SQUAD_TYPOGRAPHY.advisorCompany}px`, color: secondary, fontWeight: 600, textAlign: 'left' }}>
        {block.company}
      </Box>
    );
  }

  return null;
};

/** Web preview photo — wrapper owns aspect ratio so the <img> cannot collapse to 0 height. */
const AdvisorPhotoPreview: FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <Box
    sx={{
      width: '100%',
      aspectRatio: `${SQUAD_ADVISOR_PHOTO.widthPx} / ${SQUAD_ADVISOR_PHOTO.heightPx}`,
      overflow: 'hidden',
      // No backing colour, as in the PDF: a photo cut out of its background shows the sidebar.
      flexShrink: 0,
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
        display: 'block',
      }}
    />
  </Box>
);

export const SquadAdvisorsPreview: FC<{
  advisors?: SquadAdvisorRecord[];
  primary?: string;
  secondary?: string;
  footerLogoUrl?: string;
}> = ({
  advisors = [],
  primary = SQUAD_COLORS.navy,
  secondary = SQUAD_COLORS.gold,
  footerLogoUrl,
}) => {
  const blocks = buildAdvisorBlocks(advisors);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', color: primary, minWidth: 0 }}>
      {blocks.map((block, blockIndex) => {
        if (block.type !== 'featured') return null;
        return (
          <Box key={`featured-${blockIndex}`} sx={previewStyles.advisorBlockFeatured}>
            {/* Full-width photo in the 16:9 frame — see squadImageFormats */}
            {block.photoUrl ? <AdvisorPhotoPreview src={block.photoUrl} alt={block.name} /> : null}
            <Box sx={{ ...previewStyles.advisorFeaturedLogoCol, minHeight: 0, pl: 0, mt: block.photoUrl ? 0.5 : 0 }}>
              {renderCompanyMark(block, SQUAD_COLORS.navy, secondary)}
            </Box>
            <Box sx={{ ...previewStyles.advisorName, color: SQUAD_COLORS.navy }}>{block.name}</Box>
            {block.bio ? (
              <Box
                sx={{ ...previewStyles.advisorBio, color: SQUAD_COLORS.navy }}
                dangerouslySetInnerHTML={{ __html: richHtmlForPreview(block.bio) }}
              />
            ) : null}
          </Box>
        );
      })}
      {footerLogoUrl ? (
        <Box component="img" src={footerLogoUrl} alt="" sx={previewStyles.advisorFooterLogo} />
      ) : null}
    </Box>
  );
};

export const SquadAdvisorsPdf: FC<{
  advisors?: SquadAdvisorRecord[];
  styles: any;
  primary: string;
  secondary: string;
  footerLogoUrl?: string;
}> = ({
  advisors = [],
  styles,
  primary,
  secondary,
  footerLogoUrl,
}) => {
  const blocks = buildAdvisorBlocks(advisors);

  return (
    <View>
      {blocks.map((block, blockIndex) => {
        if (block.type !== 'featured') return null;
        return (
          <View key={`featured-${blockIndex}`} style={styles.advisorBlockFeatured}>
            {block.photoUrl ? (
              <Image src={block.photoUrl} style={styles.advisorFeaturedPhoto} />
            ) : null}
            <View style={{ ...styles.advisorFeaturedLogoCol, minHeight: 0, paddingLeft: 0 }}>
              {renderCompanyMark(block, SQUAD_COLORS.navy, secondary, true, styles)}
            </View>
            <Text style={[styles.advisorName, { color: SQUAD_COLORS.navy }]}>{block.name}</Text>
            {block.bio ? (
              <RichHtmlPdf
                html={block.bio}
                style={{ ...styles.advisorBio, color: SQUAD_COLORS.navy }}
              />
            ) : null}
          </View>
        );
      })}
      {footerLogoUrl ? <Image src={footerLogoUrl} style={styles.advisorFooterLogo} /> : null}
    </View>
  );
};
